import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import { GIVESENDGO_URL, TRAILER_EMBED, TRAILER_URL } from "../lib/api";

const CLASSIFIED_IMG =
    "https://images.unsplash.com/photo-1504711331083-9c895941bf81?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxjbGFzc2lmaWVkJTIwZG9jdW1lbnRzJTIwdG9wJTIwc2VjcmV0fGVufDB8fHx8MTc3ODE4Mjc3N3ww&ixlib=rb-4.1.0&q=85";

const Section = ({ label, children }) => (
    <div className="mb-20">
        <p className="font-accent uppercase tracking-[0.3em] text-blood text-base mb-6">
            / {label}
        </p>
        {children}
    </div>
);

export default function About() {
    return (
        <div data-testid="about-page" className="pt-32 pb-20">
            {/* Header */}
            <section className="max-w-7xl mx-auto px-6 sm:px-8 mb-20">
                <p className="font-mono text-base uppercase tracking-[0.3em] text-neutral-500 mb-6">
                    File 02 / The Brief
                </p>
                <h1
                    data-testid="about-title"
                    className="font-display text-6xl sm:text-8xl lg:text-9xl uppercase leading-[0.95] tracking-tighter text-neutral-900 text-balance"
                >
                    The film.
                    <br />
                    <span className="italic text-blood font-light">The truth.</span>
                </h1>
                <p className="font-mono text-xl sm:text-2xl text-neutral-600 mt-8 max-w-3xl leading-relaxed">
                    State of Deception is a suspenseful period piece of historical fiction —
                    a feature film grounded in character, consequence, and restraint.
                    Below is the dossier.
                </p>
            </section>

            {/* Trailer embed */}
            <section
                data-testid="about-trailer"
                className="max-w-7xl mx-auto px-6 sm:px-8 mb-24"
            >
                <div className="relative aspect-video border border-neutral-300 overflow-hidden">
                    <iframe
                        src={TRAILER_EMBED}
                        title="Proof of Concept Trailer"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="w-full h-full"
                    />
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4">
                    <p className="font-mono text-base uppercase tracking-[0.3em] text-neutral-500">
                        Proof of Concept Trailer / State of Deception, LLC
                    </p>
                    <a
                        href={TRAILER_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="about-trailer-youtube-link"
                        className="font-mono text-base uppercase tracking-[0.2em] text-neutral-600 hover:text-blood transition-colors"
                    >
                        Watch on YouTube →
                    </a>
                </div>
            </section>

            {/* Synopsis */}
            <section className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
                <div className="lg:col-span-8">
                    <Section label="Synopsis">
                        <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-neutral-900 leading-tight uppercase tracking-tight text-balance">
                            What if none of it was an accident?
                        </h2>
                        <div className="mt-8 space-y-6 font-mono text-lg sm:text-xl text-neutral-700 leading-loose">
                            <p>
                                A post-COVID narrative feature on what actually happened to our country —
                                and what could have. Indeed, if we just told you about the last decade,
                                you probably wouldn&apos;t believe it. So we&apos;ve decided to{" "}
                                <span className="text-blood italic">show</span> it to you.
                            </p>
                            <p>
                                Written by a former member of Congress, author and talk show host,
                                State of Deception tells the harrowing story of ‘deep state’ spying,
                                secret lab leaks, riots, and orchestrated pandemic psychosis that
                                harassed, censored and isolated young and old alike. They even shut
                                down our churches.
                            </p>
                            <p>
                                In a Capitol ruled by corrupt media and money, a newly-elected
                                Midwestern Senator stumbles upon a vast conspiracy and starts to
                                connect the dots between a nation in turmoil and a globalist cabal gone
                                ‘viral’ in more ways than one. Soon he&apos;s in a life-or-death struggle
                                in a riveting race against time to save the country — and himself.
                            </p>
                        </div>
                    </Section>

                    <Section label="Why This Film">
                        <p className="font-mono text-lg sm:text-xl text-neutral-700 leading-loose">
                            This is not a documentary. It&apos;s a feature film grounded in character,
                            consequence, and restraint — and yes, the power of prayer. The goal is
                            not to merely inform, but to put the audience inside the pressure and let
                            them feel what it costs to do the right thing when the system is built to
                            prevent it.
                        </p>
                        <p className="font-mono text-lg sm:text-xl text-neutral-700 leading-loose mt-6">
                            We believe stories carry moral weight. Politics is downstream of culture
                            and our culture is shaped by the stories we tell — and the ones others
                            are afraid to tell.
                        </p>
                    </Section>

                    <Section label="Why We're Crowdfunding">
                        <p className="font-mono text-lg sm:text-xl text-neutral-700 leading-loose">
                            Independent films that challenge powerful institutions are rarely funded
                            through traditional channels. They are diluted, softened, or never made
                            at all. Crowdfunding allows us to protect the integrity of this story,
                            retain creative independence, and build the film alongside people who
                            believe telling the truth about this troubled time is important.
                        </p>
                        <p className="font-mono text-lg sm:text-xl text-neutral-700 leading-loose mt-6">
                            Every contribution — large or small — is an act of stewardship.
                        </p>
                    </Section>

                    <Section label="What You're Supporting">
                        <ul className="font-mono text-lg sm:text-xl text-neutral-700 space-y-3 leading-relaxed">
                            {[
                                "Principal photography using real-world, contained locations",
                                "Professional cast and crew",
                                "Post-production (editing, sound, color, and music)",
                                "Festival preparation and distribution outreach",
                            ].map((item) => (
                                <li
                                    key={item}
                                    className="flex gap-4 border-l-2 border-blood pl-5"
                                >
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </Section>
                </div>

                {/* SIDEBAR */}
                <aside className="lg:col-span-4 space-y-12 lg:sticky lg:top-28 self-start">
                    <div className="border border-neutral-300 bg-ink2 p-8">
                        <p className="font-accent uppercase tracking-[0.3em] text-blood text-base mb-4">
                            / Creator
                        </p>
                        <h3 className="font-display text-5xl text-neutral-900">Jason Lewis</h3>
                        <p className="font-mono text-base uppercase tracking-widest text-neutral-500 mt-2">
                            Writer / Producer
                        </p>
                        <div className="w-12 h-px bg-blood my-6" />
                        <p className="font-mono text-lg text-neutral-600 leading-relaxed">
                            A former Member of Congress, author, and nationally syndicated talk show
                            host. As a former broadcaster, he knows the shelf life of a TV talk show
                            is about a day. State of Deception is something more lasting.
                        </p>
                    </div>

                    <div className="relative aspect-[3/4] overflow-hidden border border-neutral-300">
                        <img
                            src={CLASSIFIED_IMG}
                            alt="Classified files"
                            className="w-full h-full object-cover grayscale opacity-80"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-ink to-transparent" />
                        <div className="absolute bottom-6 left-6 right-6">
                            <Quote size={28} className="text-blood mb-3" />
                            <p className="font-display text-3xl text-neutral-900 italic leading-snug">
                                “Stories shape the culture long before laws do.”
                            </p>
                        </div>
                    </div>

                    <div className="border border-blood/40 bg-blood/5 p-8">
                        <p className="font-accent uppercase tracking-[0.3em] text-blood text-base mb-4">
                            / Take Action
                        </p>
                        <p className="font-mono text-lg text-neutral-700 leading-relaxed mb-6">
                            Every dollar moves the film closer to completion — and closer to an audience.
                        </p>
                        <a
                            href={GIVESENDGO_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="about-fund-btn"
                            className="inline-flex w-full items-center justify-center gap-3 bg-blood text-white font-accent uppercase tracking-widest text-lg py-4 px-6 hover:bg-bloodHover transition-colors"
                        >
                            Fund the Film <ArrowRight size={16} />
                        </a>
                        <Link
                            to="/get-involved#casting"
                            data-testid="about-cast-btn"
                            className="inline-flex w-full items-center justify-center gap-3 mt-3 bg-transparent text-neutral-900 font-accent uppercase tracking-widest text-lg py-4 px-6 hover:bg-black/5 transition-colors border border-neutral-400"
                        >
                            Audition for a Role
                        </Link>
                    </div>
                </aside>
            </section>
        </div>
    );
}
